import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import type { ListCount } from '@up2d8/contracts';
import type { AdminIconName } from '../../modules/admin-kit';
import type { Session } from '../../shared/session';

/**
 * De navigatie van de beheerschil (BHR-0, basis-spec A §3).
 *
 * Start los bovenaan, daaronder vijf groepen: Medewerkers · Communicatie · Werkvloer · Opvolging ·
 * Instellingen, en Demo als er iets in staat. Elke rechtencontrole is dezelfde als in het menu van
 * vóór BHR-0: wat iemand niet mag, staat er niet, en een groep zonder items ook niet.
 */

export interface AdminNavItem {
  key: string;
  label: string;
  href: string;
  icon: AdminIconName;
  /** De teller-badge (`#B85B1F`); weg als hij nul of onbekend is. */
  count?: number;
}

export interface AdminNavGroup {
  key: string;
  label: string;
  items: AdminNavItem[];
}

export interface AdminNav {
  /** Start, los bovenaan; in de platformzone alle drie de items. */
  top: AdminNavItem[];
  groups: AdminNavGroup[];
}

/** Een item, of niets als het recht ontbreekt. */
function when(allowed: boolean, item: AdminNavItem): AdminNavItem[] {
  return allowed ? [item] : [];
}

export interface AdminCounts {
  posts?: number;
  floorReports?: number;
  submissions?: number;
  moderation?: number;
}

export function adminNav(
  session: Session,
  t: TFunction,
  counts: AdminCounts,
  // De vlaggen van het bedrijf (ORG-1): een module die de klant uitzette, hoort niet in het menu.
  // Standaard "aan", zoals `useTenantFeatures` bij twijfel.
  isOn: (flagKey: string) => boolean = () => true,
): AdminNav {
  const has = (right: string) => session.has(right);

  const groups: AdminNavGroup[] = [
    {
      key: 'people',
      label: t('beheer.shell.groups.people'),
      items: [
        ...when(has('directory.person.view'), {
          key: 'people',
          label: t('people.title'),
          href: '/admin/people',
          icon: 'people',
        }),
        ...when(has('directory.group.manage'), {
          key: 'groups',
          label: t('groups.title'),
          href: '/admin/groups',
          icon: 'groups',
        }),
        /*
          Vestigingen staan bij de mensen en niet bij de instellingen (Stap 29): een vestiging is
          waar mensen werken. Alleen voor wie ze mag beheren; lezen gebeurt in filters en kiezers.
        */
        ...when(has('tenants.site.manage'), {
          key: 'sites',
          label: t('sites.title'),
          href: '/admin/vestigingen',
          icon: 'building',
        }),
        ...when(has('directory.field.manage'), {
          key: 'fields',
          label: t('fields.title'),
          href: '/admin/fields',
          icon: 'tag',
        }),
        ...when(has('access.role.view'), {
          key: 'roles',
          label: t('roles.title'),
          href: '/admin/roles',
          icon: 'key',
        }),
        ...when(has('identity.invitation.view'), {
          key: 'invitations',
          label: t('invitations.title'),
          href: '/admin/invitations',
          icon: 'mail',
        }),
        ...when(has('import.job.manage'), {
          key: 'imports',
          label: t('imports.title'),
          href: '/admin/imports',
          icon: 'upload',
        }),
      ],
    },
    {
      key: 'communication',
      label: t('beheer.shell.groups.communication'),
      items: [
        // Ook voor wie alleen goedkeurt: die opent ingediende berichten in dezelfde lijst (FBK-2).
        ...when(has('communication.post.draft') || has('communication.post.approve'), {
          key: 'posts',
          label: t('posts.title'),
          href: '/admin/posts',
          icon: 'megaphone',
          count: counts.posts,
        }),
        // Ook voor wie alleen mag afvinken: een teamleider plant niets, maar heeft de
        // aanwezigheidslijst wél nodig (Stap 25).
        ...when(has('agenda.item.manage') || has('agenda.attendance.view'), {
          key: 'agenda',
          label: t('agendaAdmin.title'),
          href: '/admin/agenda',
          icon: 'calendar',
        }),
        ...when(has('documents.document.manage'), {
          key: 'documents',
          label: t('documentsAdmin.title'),
          href: '/admin/documenten',
          icon: 'folder',
        }),
        ...when(has('forms.form.manage'), {
          key: 'forms',
          label: t('formsAdmin.title'),
          href: '/admin/formulieren',
          icon: 'clipboardList',
        }),
        ...when(has('shortcuts.shortcut.manage'), {
          key: 'shortcuts',
          label: t('shortcutsAdmin.title'),
          href: '/admin/shortcuts',
          icon: 'link',
        }),
      ],
    },
    {
      key: 'floor',
      label: t('beheer.shell.groups.floor'),
      items: [
        // Op het recht om op te volgen en niet om te beheren: een teamleider ziet de lijst
        // zonder sjablonen te kunnen wijzigen (Stap 38).
        ...when(has('tasks.run.view') && isOn('tasks.checklists'), {
          key: 'checklists',
          label: t('checklistsAdmin.title'),
          href: '/admin/checklists',
          icon: 'clipboardCheck',
        }),
        // Machines en diensten per lijn (Stap 39); het scherm zelf staat in de app.
        ...when(has('shift_handover.assets.manage') && isOn('shift_handover.handovers'), {
          key: 'shift-handover',
          label: t('handoverAdmin.title'),
          href: '/admin/overdracht',
          icon: 'rotate',
        }),
        // Opvolgen is het recht dat de lijst nodig heeft; inrichten kan erbij (Stap 36).
        ...when(has('floor_reports.report.handle') && isOn('floor_reports.report_button'), {
          key: 'floor-reports',
          label: t('floorReportsAdmin.title'),
          href: '/admin/meldingen',
          icon: 'alert',
          count: counts.floorReports,
        }),
        ...when(has('identity.kiosk.manage') && isOn('identity.kiosk'), {
          key: 'kiosks',
          label: t('kiosksAdmin.title'),
          href: '/admin/kiosks',
          icon: 'tablet',
        }),
        ...when(has('signage.screen.manage') && isOn('signage.screens'), {
          key: 'signage',
          label: t('signage.title'),
          href: '/admin/schermen',
          icon: 'monitor',
        }),
        ...when(has('emergency.incident.view') && isOn('emergency.channel'), {
          key: 'emergency',
          label: t('emergency.title'),
          href: '/admin/noodkanaal',
          icon: 'siren',
        }),
      ],
    },
    {
      key: 'follow-up',
      label: t('beheer.shell.groups.followUp'),
      items: [
        ...when(has('reporting.report.view'), {
          key: 'reports',
          label: t('reports.title'),
          href: '/admin/reports',
          icon: 'chart',
        }),
        // Apart van de bouwer: een teamleider behandelt aanvragen en bouwt geen formulieren.
        ...when(has('forms.submission.handle'), {
          key: 'submissions',
          label: t('formsInbox.title'),
          href: '/admin/aanvragen',
          icon: 'inbox',
          count: counts.submissions,
        }),
        ...when(has('interaction.comment.moderate'), {
          key: 'moderation',
          label: t('moderation.title'),
          href: '/admin/moderatie',
          icon: 'flag',
          count: counts.moderation,
        }),
        ...when(has('audit.trail.view'), {
          key: 'audit',
          label: t('audit.title'),
          href: '/admin/audit',
          icon: 'history',
        }),
        // Ingeplande berichten en checklists (Stap 14).
        ...when(has('scheduling.schedule.manage'), {
          key: 'schedules',
          label: t('scheduling.title'),
          href: '/admin/schedules',
          icon: 'clock',
        }),
      ],
    },
    {
      key: 'settings',
      label: t('beheer.shell.groups.settings'),
      items: [
        ...when(has('tenants.theme.manage'), {
          key: 'branding',
          label: t('branding.title'),
          href: '/admin/huisstijl',
          icon: 'palette',
        }),
      ],
    },
    /*
      De groep "Demo" bestaat alleen als er iets in staat (Extra 2, punt 9).

      "Voorbeeldnotities" blijft achter zijn recht (`example.note.view`) en de featureflag; bij een
      klant staat dat recht nergens aan. "Designsysteem" staat sinds Extra 1 op `/master/design`.
    */
    {
      key: 'demo',
      label: t('beheer.shell.groups.demo'),
      items: [
        ...when(has('example.note.view'), {
          key: 'example',
          label: t('notes.title'),
          href: '/admin/example',
          icon: 'beaker',
        }),
      ],
    },
  ];

  return {
    top: [
      { key: 'admin-dashboard', label: t('beheer.shell.start'), href: '/admin', icon: 'start' },
    ],
    groups: groups.filter((group) => group.items.length > 0),
  };
}

/**
 * Master (sinds MST-1 de platformzone): Start los bovenaan, daarna twee groepen. "Klanten" gaat over
 * de organisaties, "Platform" over het product zelf (spec A §3, MRK-3, AUD-1, bundel MST).
 *
 * `has` is optioneel zodat een aanroeper zonder sessie blijft werken; zonder `has` staat alles er
 * (elke platformbeheerder heeft elk platformrecht). Met een sessie alleen voor wie het recht
 * heeft; `zone-layout.tsx` geeft `session.has` mee.
 */
export function platformNav(t: TFunction, has?: (right: string) => boolean): AdminNav {
  const mag = (right: string) => (has ? has(right) : true);
  return {
    top: [
      {
        key: 'platform-dashboard',
        label: t('beheer.shell.start'),
        href: '/master',
        icon: 'start',
      },
    ],
    groups: [
      {
        key: 'klanten',
        label: t('master.nav.customers'),
        items: [
          {
            key: 'platform-tenants',
            label: t('tenants.title'),
            href: '/master/organisaties',
            icon: 'building',
          },
          // MST-6: pakketten, omzet en upsell.
          ...when(mag('tenants.subscription.manage'), {
            key: 'platform-subscriptions',
            label: t('master.nav.subscriptions'),
            href: '/master/abonnementen',
            icon: 'chart',
          }),
        ],
      },
      {
        key: 'platform',
        label: t('master.nav.platform'),
        items: [
          // MST-4: functies over alle organisaties heen.
          ...when(mag('tenants.feature.manage'), {
            key: 'platform-features',
            label: t('master.nav.features'),
            href: '/master/functies',
            icon: 'flag',
          }),
          // ORG-3: het startprofiel per type organisatie.
          ...when(mag('tenants.tenant.manage'), {
            key: 'platform-org-types',
            label: t('beheer.orgType.page.nav'),
            href: '/master/organisatietypes',
            icon: 'wrench',
          }),
          // MST-7: wie Master mag gebruiken.
          ...when(mag('identity.platform_admin.manage'), {
            key: 'platform-admins',
            label: t('master.nav.admins'),
            href: '/master/beheerders',
            icon: 'shieldCheck',
          }),
          // MST-8: database, worker, outbox en mail.
          ...when(mag('platform.system.view'), {
            key: 'platform-system',
            label: t('master.nav.system'),
            href: '/master/systeem',
            icon: 'monitor',
          }),
          // Het merk van het product (MRK-3): alleen het platformbeheer, met het recht ervoor.
          ...when(mag('tenants.brand.manage'), {
            key: 'platform-brand',
            label: t('beheer.merk.nav'),
            href: '/master/merk',
            icon: 'tag',
          }),
          // Het spoor van het platform (AUD-1, ADR-0088): wat wij deden, en wat geen bedrijf heeft.
          ...when(mag('platform.audit.view'), {
            key: 'platform-audit',
            label: t('beheer.platformAudit.nav'),
            href: '/master/audit',
            icon: 'history',
          }),
          // Het designsysteem hoort bij ons en niet bij de klant (Extra 1).
          { key: 'design', label: t('nav.designSystem'), href: '/master/design', icon: 'palette' },
        ],
      },
    ],
  };
}

/** Tellers blijven een minuut geldig: ze staan op elke pagina, en ze hoeven niet op de seconde. */
const COUNT_STALE = 60_000;

/**
 * De tellers in de zijbalk.
 *
 * Elke teller vraagt een telroute (`…/count`, OPR-3) met dezelfde filters en hetzelfde recht als de
 * lijst van het scherm. Tot OPR-3 telde hij de lijst zelf, en die is begrensd (100 berichten, 200
 * meldingen of aanvragen, 50 gemelde reacties): bij meer wachtend werk stond er een te laag getal.
 *
 * Elke teller staat onder een sleutel die met die van het scherm waar hij naartoe wijst **begint**
 * en op `'nav'` eindigt. Een `invalidateQueries` na een actie op dat
 * scherm werkt de teller dus vanzelf bij, maar de minuut `staleTime` van de teller raakt het
 * scherm niet: dezelfde sleutel delen deed dat wel, en dan toonde de inbox een aanvraag van
 * daarnet pas na een minuut (gevonden met `forms.spec.ts`).
 *
 * - Berichten: "te keuren" (`pending_approval`), alleen met `communication.post.approve`; dezelfde
 *   vraag als het tabblad "Te keuren" in `posts-page.tsx`.
 * - Meldingen: de nieuwe (`new`) meldingen; met `floor_reports.report.manage` die van iedereen.
 * - Aanvragen: de ingediende (`submitted`), zoals de inbox.
 * - Moderatie: de gerapporteerde reacties, zoals de moderatiepagina.
 */
export function useAdminCounts(session: Session, enabled: boolean): AdminCounts {
  const { api } = session;
  const has = (right: string) => enabled && session.has(right);

  const posts = useQuery({
    queryKey: ['posts', 'pending-count', 'nav'],
    enabled: has('communication.post.approve'),
    staleTime: COUNT_STALE,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/posts/count', {
        params: { query: { status: 'pending_approval' } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ListCount).count;
    },
  });

  const canManageReports = session.has('floor_reports.report.manage');
  const floorReports = useQuery({
    queryKey: ['floor-reports', 'open-count', canManageReports, 'nav'],
    enabled: has('floor_reports.report.handle'),
    staleTime: COUNT_STALE,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/floor-reports/count', {
        params: {
          query: { status: 'new', ...(canManageReports ? { all: 'true' as const } : {}) },
        },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ListCount).count;
    },
  });

  const submissions = useQuery({
    queryKey: ['forms', 'inbox', 'nav'],
    enabled: has('forms.submission.handle'),
    staleTime: COUNT_STALE,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/forms/submissions/count', {
        params: { query: { status: 'submitted' } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ListCount).count;
    },
  });

  const moderation = useQuery({
    queryKey: ['moderation', 'nav'],
    enabled: has('interaction.comment.moderate'),
    staleTime: COUNT_STALE,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/moderation/comments/count', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ListCount).count;
    },
  });

  return {
    posts: posts.data,
    floorReports: floorReports.data,
    submissions: submissions.data,
    moderation: moderation.data,
  };
}
